/**
 * Runtime API response validation using Zod schemas.
 *
 * parseResponse<T>(schema, data) validates the JSON body returned by the
 * backend against a Zod schema. On success it returns the typed value;
 * on failure it logs a structured error message and throws an
 * ApiValidationError so the caller can distinguish validation failures
 * from network/HTTP errors.
 *
 * NOTE: This module deliberately does NOT import from ./client to avoid
 * circular-mock issues in tests. ApiValidationError is a standalone Error
 * subclass with the same .code / .status shape as ApiError so that
 * existing error-handling code can treat them uniformly.
 */

import { z, type ZodType } from "zod";
import { createLogger } from '@/utils/logger';

const logger = createLogger('APIValidation');

/**
 * Typed error for API response validation failures.
 *
 * Mirrors the ApiError shape (code, status, details) so that generic
 * error-handling code (TanStack Query onError, error boundaries) can
 * branch on .code === "VALIDATION_ERROR" without needing to import
 * ApiError itself.
 */
export class ApiValidationError extends Error {
  public readonly name = "ApiValidationError";
  /** Matches ApiError.code for uniform error handling. */
  public readonly code = "VALIDATION_ERROR" as const;
  /** HTTP status 0 signals "not an HTTP error". */
  public readonly status = 0 as const;
  /** The raw Zod issues array for programmatic inspection. */
  public readonly issues: z.ZodIssue[];
  /** Extra details bag, mirrors ApiError.details. */
  public readonly details: { issues: z.ZodIssue[] };

  constructor(
    message: string,
    issues: z.ZodIssue[],
  ) {
    super(message);
    this.issues = issues;
    this.details = { issues };
  }
}

/**
 * Validate an API response against a Zod schema.
 *
 * @param schema - The Zod schema that describes the expected shape.
 * @param data   - The parsed JSON body from the HTTP response.
 * @param context - Human-readable label for log messages (e.g. "GET /sessions").
 * @returns The validated and typed data.
 * @throws {ApiValidationError} when the data does not match the schema.
 */
export function parseResponse<T>(
  schema: ZodType<T>,
  data: unknown,
  context?: string,
): T {
  const result = schema.safeParse(data);

  if (result.success) {
    return result.data;
  }

  // Build a human-readable summary of what went wrong.
  const issueLines = result.error.issues.map(
    (issue: z.ZodIssue) =>
      `  - path: ${issue.path.join(".")} | code: ${issue.code} | message: ${issue.message}`,
  );

  const label = context ? ` [${context}]` : "";

  // Structured console.error so developers can copy-paste into bug reports.
  // NOTE: Raw data is intentionally NOT logged to avoid PII leaks
  // (auth tokens, emails, etc.) to the browser console and log drains.
  logger.error(
    `${label} Response does not match expected schema.\n` +
      `Issues (${result.error.issues.length}):\n` +
      issueLines.join("\n") +
      "\n" +
      `Data type: ${typeof data}, keys: ${data && typeof data === "object" ? Object.keys(data as Record<string, unknown>).length : "N/A"}`,
  );

  throw new ApiValidationError(
    `API response validation failed${label}: ${result.error.issues.length} issue(s)`,
    result.error.issues,
  );
}
